import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { MultiSectionDigitalClock } from "@mui/x-date-pickers/MultiSectionDigitalClock";
import { BottomSheet, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { type ReactNode, useState } from "react";

import { Button } from "../components/Button";
import { DialogFooter } from "../components/DialogFooter";

interface DigitalClockInputBottomSheetProps {
  modalState: UseModalState;
  title?: ReactNode;
  subtitle?: ReactNode;
  initialTime?: Date;
  confirmButtonText?: string;
  minutesStep?: number;
  onConfirm: (date: Date) => void;
  onBack?: () => void;
  children?: ReactNode;
}

export function DigitalClockInputBottomSheet(props: DigitalClockInputBottomSheetProps) {
  const {
    modalState,
    title,
    subtitle,
    initialTime,
    confirmButtonText = "Next",
    minutesStep = 5,
    onConfirm,
    onBack,
    children,
  } = props;

  const [selectedTime, setSelectedTime] = useState<Date | undefined>(initialTime);

  return (
    <BottomSheet
      disableSwipeArea
      // canDismiss must be false, otherwise scrolling the clock will close the bottom sheet
      canDismiss={false}
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical" onClose={modalState.closeModal}>
          <Button
            fullWidth
            variant="contained"
            disabled={!isDefined(selectedTime)}
            onClick={() => {
              if (!selectedTime) {
                return;
              }

              onConfirm(selectedTime);
            }}
          >
            {confirmButtonText}
          </Button>
          {isDefined(onBack) && (
            <Button fullWidth variant="outlined" onClick={onBack}>
              Back
            </Button>
          )}
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack width="100%" spacing={4} alignItems="center">
          {isDefined(title) && title}
          {isDefined(subtitle) && subtitle}
          {isDefined(children) && children}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MultiSectionDigitalClock
              ampm
              skipDisabled
              value={selectedTime}
              timeSteps={{ minutes: minutesStep }}
              sx={{
                justifyContent: "center",
                "& .MuiList-root": {
                  scrollbarWidth: "none",
                },
              }}
              onChange={(newTime: Date | undefined) => {
                if (isDefined(newTime)) {
                  setSelectedTime(newTime);
                }
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
